<script lang="ts">
	import { onMount, tick } from "svelte";

	/**
	 * The initial font size of the text, in `em` units.
	 * @type {number}
	 * @default 1.75
	 */
	export let textSize = 1.75;

	/**
	 * The minimum font size of the text, in `em` units.
	 * @type {number}
	 * @default 0.65
	 */
	export let minTextSize = 0.65;

	/**
	 * Whether the text size has been calculated.
	 * @type {boolean}
	 * @default false
	 */
	export let calculated = false;

	/**
	 * The inner width of the window.
	 * @type {number}
	 */
	let innerWidth = 0;

	/**
	 * Reference to the text container element.
	 * @type {HTMLElement}
	 */
	let ref: HTMLElement;

	/**
	 * Reference to the parent container element.
	 * @type {HTMLElement}
	 */
	let ref2: HTMLElement;

	/**
	 * The difference between the parent container's dimensions and the text container's dimensions.
	 * @type {number}
	 */
	let difference = 0;

	/**
	 * Calculates the appropriate font size for the text based on the container's dimensions.
	 * @param {DOMRect} boundingClientRect - The bounding rectangle of the text container.
	 * @param {DOMRect} parentBoundingClientRect - The bounding rectangle of the parent container.
	 */
	async function getTextSize(
		boundingClientRect: DOMRect,
		parentBoundingClientRect: DOMRect
	) {
		if (!parentBoundingClientRect || !boundingClientRect) {
			return;
		}

		// Calculate the height difference between the parent and text container.
		difference = parentBoundingClientRect.height - boundingClientRect.height;

		// Adjust the text size if the text overflows the parent container's height.
		if (difference < 0) {
			textSize =
				(textSize / boundingClientRect.height) *
				parentBoundingClientRect.height;
			if (textSize < minTextSize) {
				textSize = minTextSize;
			}
		}

		// Wait for the DOM to update.
		await tick();

		// Access the children of the text container.
		const children = ref.children[0].children;

		// Adjust the text size based on the width of each child element.
		Array.from(children).forEach((child) => {
			const newBoundClientRect = (
				child as HTMLElement
			)?.getBoundingClientRect();
			difference = parentBoundingClientRect.width - newBoundClientRect.width;

			// Adjust the text size if the text overflows the parent container's width.
			if (difference < 0) {
				textSize =
					(textSize / newBoundClientRect.width) *
					parentBoundingClientRect.width;
			}

			// Adjust the text size if the text exceeds the screen width.
			if (newBoundClientRect.width > innerWidth) {
				textSize = (textSize / newBoundClientRect.width) * innerWidth - 0.2;
			}
		});

		// Mark the text size as calculated.
		calculated = true;
	}

	// Reactively calculate the text size when dependencies change.
	$: {
		setTimeout(() => {
			getTextSize(ref?.getBoundingClientRect(), ref2?.getBoundingClientRect());
		}, 0);
	}

	// Calculate the text size on component mount.
	onMount(() => {
		getTextSize(ref?.getBoundingClientRect(), ref2?.getBoundingClientRect());
	});
</script>

<svelte:window bind:innerWidth />

<div class="{$$props.class} relative" bind:this={ref2}>
	<div
		class="max-w-screen relative
		{!calculated ? 'invisible' : 'visible'}
		"
		bind:this={ref}
		style="font-size: {textSize}em"
	>
		<slot />
	</div>
</div>
